import { Box, Grid } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import apis from "../../services";
import { DataGrid } from "@mui/x-data-grid";
import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import Container from "@mui/material/Container";
import { toast } from "react-toastify";
import ShareholderTable from "components/datagrid/ShareholderTable";
import useNavigate from "hooks/Navigate";
import { Form, Col, Row, Button, Dropdown } from "react-bootstrap";
import { CiSearch } from "react-icons/ci";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Card, CardContent, Divider, Typography } from "@mui/material";
import { useSelector } from "react-redux";

const PAGE_LIMIT = 20;

const parseCsvRow = (row) => {
  const cells = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < row.length; index += 1) {
    const character = row[index];
    if (character === '"') {
      if (quoted && row[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === "," && !quoted) {
      cells.push(cell);
      cell = "";
    } else {
      cell += character;
    }
  }

  cells.push(cell);
  return cells;
};

const serializeCsvRow = (cells) =>
  cells
    .map((cell) => {
      const value = String(cell ?? "");
      return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    })
    .join(",");

const formatListingCsv = (csv) => {
  const lines = String(csv).split("\n");
  const headerIndex = lines.findIndex((line) => line.includes("Shareholder ID"));
  if (headerIndex === -1) return csv;

  const headers = parseCsvRow(lines[headerIndex]);
  const numericColumns = new Set(
    ["Total Shares", "Series G Share", "Series F Share", "Total Investment", "Total Dividends"]
      .map((label) => headers.indexOf(label))
      .filter((index) => index !== -1)
  );

  return lines
    .map((line, index) => {
      if (index <= headerIndex || !line.trim()) return line;

      const cells = parseCsvRow(line);
      numericColumns.forEach((columnIndex) => {
        const numericValue = Number(cells[columnIndex]);
        if (!Number.isNaN(numericValue)) cells[columnIndex] = Math.floor(numericValue).toFixed(1);
      });
      return serializeCsvRow(cells);
    })
    .join("\n");
};

export const ShareholderRegistry = () => {
  const [shareholders, setShareholders] = useState([]);
  const navigate = useNavigate();
  const { adminUser } = useSelector((store) => store.admin);
  const [searchInput, setSearchInput] = useState("");
  const [minInvestmentInput, setMinInvestmentInput] = useState("");
  const [maxInvestmentInput, setMaxInvestmentInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState("");
  const [dateToInput, setDateToInput] = useState("");
  const [documentStatusInput, setDocumentStatusInput] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    minInvestment: "",
    maxInvestment: "",
    dateFrom: "",
    dateTo: "",
    documentStatus: "",
  });

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: PAGE_LIMIT,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [recipientCount, setRecipientCount] = useState(0);
  const [countLoading, setCountLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderForm, setReminderForm] = useState({
    title: "",
    body: "",
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
    }, 500);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const handleApplyFilters = () => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      minInvestment: minInvestmentInput,
      maxInvestment: maxInvestmentInput,
      dateFrom: dateFromInput,
      dateTo: dateToInput,
      documentStatus: documentStatusInput,
    }));
  };

  const handleClearFilters = () => {
    setMinInvestmentInput("");
    setMaxInvestmentInput("");
    setDateFromInput("");
    setDateToInput("");
    setDocumentStatusInput("");
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      minInvestment: "",
      maxInvestment: "",
      dateFrom: "",
      dateTo: "",
      documentStatus: "",
    }));
  };

  const fetchShareholders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apis.getShareholders({
        ...filters,
        page,
        limit: PAGE_LIMIT,
      });
      if (res?.data?.status) {
        setShareholders(res?.data?.shareholders || []);
        setPagination(
          res?.data?.pagination || {
            total: res?.data?.shareholders?.length || 0,
            page,
            limit: PAGE_LIMIT,
            totalPages: 1,
          }
        );
      } else {
        toast.error(res?.data?.message || "Unable to load shareholders");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to load shareholders");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchShareholders();
  }, [fetchShareholders]);

  const fetchReminderCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const res = await apis.getShareholders({
        documentStatus: "not_uploaded",
        page: 1,
        limit: 1,
      });

      if (res?.data?.status) {
        setRecipientCount(res?.data?.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Unable to load reminder count", error);
    } finally {
      setCountLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReminderCount();
  }, [fetchReminderCount]);

  const onPageChange = (direction) => {
    if (direction === "prev" && page === 1) return;
    if (direction === "next" && page >= pagination.totalPages) return;

    setPage((currentPage) => (direction === "next" ? currentPage + 1 : currentPage - 1));
  };

  const handleExportCsv = async (excludeEmailPhone = false) => {
    setExporting(true);
    try {
      const res = await apis.exportShareholdersCsv({
        ...filters,
        excludeEmailPhone,
      });
      const csv = typeof res.data === "string" ? res.data : await res.data.text();
      const blob = new Blob([formatListingCsv(csv)], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `shareholder-registry${
        excludeEmailPhone ? "-no-contact" : ""
      }-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.message || "Unable to export CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleReminderChange = (e) => {
    const { id, value } = e.target;
    setReminderForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleSendReminder = async (e) => {
    e.preventDefault();

    if (!reminderForm.title.trim() || !reminderForm.body.trim()) {
      toast.error("Please fill in both title and body");
      return;
    }

    try {
      setSendingReminder(true);
      const formData = new FormData();
      formData.append("target", "Documents-Not-Uploaded");
      formData.append("type", "news");
      formData.append("from", "admin");
      formData.append("to", "users");
      formData.append("title", reminderForm.title.trim());
      formData.append("body", reminderForm.body.trim());

      const response = await apis.adminSendNotification(formData);
      if (response?.data?.status) {
        toast.success(response?.data?.message || "Notification sent successfully");
        setReminderForm({
          title: "",
          body: "",
        });
      } else {
        toast.error(response?.data?.message || "Unable to send notification");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to send notification");
    } finally {
      setSendingReminder(false);
    }
  };

  const tables = ShareholderTable(shareholders, {
    navigate,
    offset: (page - 1) * PAGE_LIMIT,
  });
  const showNotificationSection =
    documentStatusInput === "not_uploaded" ||
    filters.documentStatus === "not_uploaded";

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
          className="custom-scroll"
        >
          <Header />
          {loading ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container sx={{ mt: 4, mb: 4 }} className="mainContainerComp">
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2.5,
                }}
              >
                <h3 className="dashboard-title" style={{ marginBottom: 6 }}>
                  Legal Compliance
                </h3>
                <Dropdown align="end">
                  <Dropdown.Toggle
                    variant="danger"
                    id="legal-compliance-export-options"
                    size="sm"
                    disabled={exporting}
                  >
                    {exporting ? "Exporting..." : "Export Options"}
                  </Dropdown.Toggle>

                  <Dropdown.Menu className="shadow-sm">
                    <Dropdown.Item onClick={() => handleExportCsv(false)} disabled={exporting}>
                      Export CSV
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleExportCsv(true)} disabled={exporting}>
                      Export CSV without email and phone
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Box>

              <Row className="mt-3">
                <Col xs={12} md={4} className="mb-2">
                  <Form.Group style={{ position: "relative" }}>
                    <Form.Label>Search by Name, Email, RFC or CURP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter name, email, RFC or CURP"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <div className="searchCon">
                      <CiSearch className="search-icon" color="#AFAEAE" fontSize={18} />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="align-items-end">
                <Col xs={6} md={2} className="mb-2">
                  <Form.Group>
                    <Form.Label>Min Investment</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="e.g. 5000"
                      value={minInvestmentInput}
                      onChange={(e) => setMinInvestmentInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Form.Group>
                    <Form.Label>Max Investment</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="e.g. 50000"
                      value={maxInvestmentInput}
                      onChange={(e) => setMaxInvestmentInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Form.Group>
                    <Form.Label>Date From</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateFromInput}
                      onChange={(e) => setDateFromInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Form.Group>
                    <Form.Label>Date To</Form.Label>
                    <Form.Control
                      type="date"
                      value={dateToInput}
                      onChange={(e) => setDateToInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Form.Group>
                    <Form.Label>Document Status</Form.Label>
                    <Form.Select
                      value={documentStatusInput}
                      onChange={(e) => setDocumentStatusInput(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="approved">Documents Approved</option>
                      <option value="rejected">Documents Rejected</option>
                      <option value="not_uploaded">Documents Not Uploaded</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Button variant="danger" className="w-100" onClick={handleApplyFilters}>
                    Apply Filters
                  </Button>
                </Col>
                <Col xs={6} md={2} className="mb-2">
                  <Button variant="outline-secondary" className="w-100" onClick={handleClearFilters}>
                    Clear Filters
                  </Button>
                </Col>
              </Row>

              <Grid
                item
                container
                justifyContent="flex-end"
                className="mt-4"
                sx={{ height: "600px", position: "relative", zIndex: "10" }}
              >
                <DataGrid
                  rows={tables?.rows}
                  columns={tables?.columns}
                  hideFooter
                  pageSize={PAGE_LIMIT}
                  rowsPerPageOptions={[PAGE_LIMIT]}
                  getRowClassName={(params) =>
                    params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                  }
                />
              </Grid>

              <div className="pagination-container">
                <div>Total: {pagination.total}</div>
                <div>
                  <button
                    className="paginator"
                    disabled={page === 1}
                    onClick={() => onPageChange("prev")}
                  >
                    <IoIosArrowBack />
                  </button>
                  Page {pagination.page} of {pagination.totalPages}
                  <button
                    className="paginator"
                    disabled={page >= pagination.totalPages}
                    onClick={() => onPageChange("next")}
                  >
                    <IoIosArrowForward />
                  </button>
                </div>
              </div>

              {adminUser?.type === "owner" && showNotificationSection && (
                <Card
                  sx={{
                    mt: 4,
                    borderRadius: 4,
                    border: "1px solid rgba(230, 14, 16, 0.14)",
                    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2.5,
                      background:
                        "linear-gradient(135deg, rgba(230,14,16,0.08) 0%, rgba(255,107,74,0.06) 100%)",
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#E60E10" }}>
                      Send Notifications
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2.5,
                      }}
                    >
                      <Box>
                        <Typography variant="overline" sx={{ letterSpacing: 1, color: "text.secondary" }}>
                          Recipients
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>
                          {countLoading ? "Loading..." : recipientCount}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 999,
                          backgroundColor: "rgba(230,14,16,0.08)",
                          color: "#E60E10",
                          fontWeight: 700,
                        }}
                      />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    <Form onSubmit={handleSendReminder}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Form.Group>
                          <Form.Label>Title</Form.Label>
                          <Form.Control
                            id="title"
                            type="text"
                            placeholder="Example: Please upload your documents"
                            value={reminderForm.title}
                            onChange={handleReminderChange}
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Body</Form.Label>
                          <Form.Control
                            id="body"
                            as="textarea"
                            rows={4}
                            placeholder="Example: Please upload the required documents to complete your profile."
                            value={reminderForm.body}
                            onChange={handleReminderChange}
                          />
                        </Form.Group>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          mt: 3,
                        }}
                      >
                        <Button
                          variant="danger"
                          type="submit"
                          disabled={sendingReminder || recipientCount === 0}
                        >
                          {sendingReminder ? "Sending..." : "Send Notification"}
                        </Button>
                      </Box>
                    </Form>
                  </CardContent>
                </Card>
              )}
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default ShareholderRegistry;
